/* eslint-disable import/no-anonymous-default-export */
import * as types from "./auth.actionTypes";
import { AnyAction } from "redux";
import { initialState } from "./auth.initialState";
import {
  setToken,
  setAccountType,
  deleteAllCookies,
  getAccountType,
  setCloudFrontKeyPairId,
  setCloudFrontKeyPolicy,
  setCloudFrontSignature,
} from "../../utils/cookieHelpers";
import { trackEvent } from "utils/segmentHelpers";
import {
  EVENT_LOGIN_FAILED,
  EVENT_LOGIN_SUCCESSFUL,
  EVENT_LOGOUT,
  EVENT_SIGN_UP_FAILED,
  EVENT_SIGN_UP_SUCCESSFUL,
} from "constants/events.constant";
import Router from "next/router";

export default (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case types.GET_USER_PROFILE_SUCCESS: {
      return {
        ...state,
        profile: action.payload,
      };
    }

    case types.GET_USER_MEMBERSHIP_SUCCESS: {
      return {
        ...state,
        isMembership: action.payload.status,
      };
    }

    case types.GET_USER_MEMBERSHIP_DETAILS_SUCCESS: {
      return {
        ...state,
        subscriptionDetails: action.payload,
      };
    }

    case types.LOGIN_SUCCESS: {
      trackEvent(EVENT_LOGIN_SUCCESSFUL, {
        account_type: action.payload.origin,
      });
      setAccountType(action.payload.origin);
      setToken(action.payload.accessToken);
      return {
        ...state,
      };
    }

    case types.LOGIN_FAILURE: {
      trackEvent(EVENT_LOGIN_FAILED);
      return {
        ...state,
      };
    }

    case types.LOG_OUT: {
      fetch("/api/auth/logout", { method: "POST" });

      deleteAllCookies();
      trackEvent(EVENT_LOGOUT, {
        account_type: getAccountType(),
      });

      if (typeof window !== "undefined" && window.location.pathname !== "/") {
        Router.push("/");
      }

      return {
        ...state,
        profile: null,
        isMembership: null,
        subscriptionDetails: null,
        paidMindsets: {},
      };
    }

    case types.SIGNUP_SUCCESS: {
      setToken(action.payload.accessToken);
      trackEvent(EVENT_SIGN_UP_SUCCESSFUL, {
        account_type: action.payload.origin,
      });
      return {
        ...state,
      };
    }

    case types.SIGNUP_FAILURE: {
      trackEvent(EVENT_SIGN_UP_FAILED, {
        // @ts-ignore
        message: action.payload.message,
      });
      return {
        ...state,
      };
    }

    case types.SET_USER_PROFILE: {
      const { userProfile, isMembership, subscriptionDetails, coupon } =
        action.payload;
      return {
        ...state,
        profile: userProfile,
        isMembership,
        subscriptionDetails,
        coupon,
      };
    }

    case types.SET_COUPON:
    case types.GET_COUPON_DETAILS_SUCCESS: {
      return {
        ...state,
        coupon: action.payload,
      };
    }

    case types.USER_DELETE_ACCOUNT_SUCCESS: {
      return {
        ...state,
        IsShowDeleteAccountPopup: action.payload,
      };
    }

    case types.TOGGLE_DELETE_ACCOUNT_POPUP: {
      return {
        ...state,
        IsShowDeleteAccountPopup: action.payload,
      };
    }

    case types.FETCH_LIKED_CONTENT_SUCCESS: {
      return {
        ...state,
        likedContent: action.payload.data.map(({ id }) => id),
      };
    }
    case types.LIKE_CONTENT_SUCCESS: {
      return {
        ...state,
        likedContent: [...state.likedContent, action.payload.id],
      };
    }
    case types.UNLIKE_CONTENT_SUCCESS: {
      return {
        ...state,
        likedContent: state.likedContent.filter(
          (id) => id !== action.payload.id
        ),
      };
    }

    case types.FETCH_LIKED_PHOTOS_SUCCESS: {
      return {
        ...state,
        likedPhotos: action.payload.data.map(({ id }) => id),
      };
    }
    case types.LIKE_PHOTO_SUCCESS: {
      return {
        ...state,
        likedPhotos: [...state.likedPhotos, action.payload.id],
      };
    }
    case types.UNLIKE_PHOTO_SUCCESS: {
      return {
        ...state,
        likedPhotos: state.likedPhotos.filter((id) => id !== action.payload.id),
      };
    }

    case types.GET_SIGNED_COOKIES_SUCCESS: {
      const { cloudFrontKeyPairId, cloudFrontPolicy, cloudFrontSignature } =
        action.payload;
      setCloudFrontKeyPairId(cloudFrontKeyPairId);
      setCloudFrontKeyPolicy(cloudFrontPolicy);
      setCloudFrontSignature(cloudFrontSignature);

      return { ...state, signedCookie: action.payload };
    }

    case types.GET_MINDSET_IS_PAID_SUCCESS: {
      const { slug, isPaid } = action.payload;
      return {
        ...state,
        paidMindsets: {
          ...state.paidMindsets,
          [slug]: isPaid,
        },
      };
    }

    case types.GET_STRIPE_PROMO_CODE_REQUEST: {
      return {
        ...state,
        promotionCode: {
          ...state.promotionCode,
          loading: true,
          data: {},
          errorMessage: null,
        },
      };
    }
    case types.GET_STRIPE_PROMO_CODE_SUCCESS: {
      return {
        ...state,
        promotionCode: {
          ...state.promotionCode,
          loading: false,
          data: action.payload,
          errorMessage: null,
        },
      };
    }
    case types.GET_STRIPE_PROMO_CODE_FAILURE: {
      return {
        ...state,
        promotionCode: {
          ...state.promotionCode,
          loading: false,
          data: {},
          errorMessage: action.payload,
        },
      };
    }

    case types.RESET_STRIPE_PROMO_CODE: {
      return {
        ...state,
        promotionCode: {
          loading: false,
          data: {},
          errorMessage: "",
          visible: false,
        },
      };
    }

    case types.TOGGLE_SHOW_PROMO_CODE: {
      return {
        ...state,
        promotionCode: {
          ...state.promotionCode,
          visible: !state.promotionCode.visible,
        },
      };
    }

    case types.REMOVE_PROMO_CODE: {
      return {
        ...state,
        promotionCode: {
          ...state.promotionCode,
          data: {},
        },
      };
    }

    case types.CREATE_STRIPE_SETUP_INTENT_REQUEST: {
      return {
        ...state,
        stripeSetupIntent: {
          clientSecret: null,
          publicKey: null,
          errorMessage: null,
        },
      };
    }

    case types.CREATE_STRIPE_SETUP_INTENT_SUCCESS: {
      const { clientSecret, publicKey } = action.payload;
      return {
        ...state,
        stripeSetupIntent: {
          clientSecret,
          publicKey,
          errorMessage: null,
        },
      };
    }

    case types.CREATE_STRIPE_SETUP_INTENT_FAILURE: {
      return {
        ...state,
        stripeSetupIntent: {
          clientSecret: null,
          publicKey: null,
          errorMessage: action.payload,
        },
      };
    }

    default:
      return state;
  }
};
